// Remuneração da equipe de vendas (VC) — puro.
//
// Responde a pergunta do fim do mês: quanto pagar a cada pessoa.
//
//   pagamento = comparecimentos × valor fixo  +  recebido no mês × percentual
//
// As duas parcelas vêm de fatos diferentes, e é isso que torna o cálculo
// delicado: o comparecimento é um evento único do lead, e o recebimento é
// dinheiro que continua chegando MESES depois do contrato fechado.

import { creditoSdr } from '@/metas/credito';
// ★★ WK: a regra do comparecimento mora no módulo neutro (ver o bloco abaixo).
import {
  creditoComparecimento,
  dataComparecimento,
  indexarAgendamentosPorLead,
  indexarEquipe,
} from '@/leads/contagemComparecimento';

/** Sem importar `calculo.js` das metas (que importaria de volta): cópia mínima. */
const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const numero = (v) => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const centavos = (v) => Math.round(numero(v) * 100) / 100;

export const MODELOS = [
  { id: 'fixo', label: 'Só fixo por comparecimento' },
  { id: 'fixo_percentual', label: 'Fixo por comparecimento + % do recebido' },
];

export const SEM_RESPONSAVEL = 'Sem responsável';

/**
 * ★★★ PC-6 P8 — A COMISSÃO DO RECEBIDO PASSA A SER DA SDR ATUAL, a partir de outubro/2026.
 *
 * ⚠⚠ MUDANÇA DE DINHEIRO, decidida pelo dono em 24/10/2026: de `mes >= MES_CORTE_COMISSAO`
 * em diante, a ordem é `sdrId → sdrOriginalId → Sem responsável`. Antes do corte vale a regra
 * antiga (`creditoSdr`, o original) — ★ setembro foi pago assim e o snapshot não pode mudar.
 *
 * ★ Isso só é justo porque a posse TRAVOU (PC-6 P1): sem a trava, a SDR que se atribuísse um
 * lead fechado antigo levaria a comissão dele. As duas coisas sobem juntas, nunca separadas.
 *
 * ⚠ Comparecimento NÃO usa isto: segue a A-10.4 (quem agendou).
 */
export const MES_CORTE_COMISSAO = '2026-10';

export function creditoComissao(lead, mes) {
  if (!(String(mes ?? '') >= MES_CORTE_COMISSAO)) return creditoSdr(lead);
  if (lead?.sdrId) return { id: lead.sdrId, nome: lead.sdrNome || '' };
  if (lead?.sdrOriginalId) return { id: lead.sdrOriginalId, nome: lead.sdrOriginalNome || '' };
  return { id: '', nome: SEM_RESPONSAVEL };
}

/**
 * ★ PC-6 P9 — só se reabre mês DO CORTE EM DIANTE. ⚠ Reabrir setembro recalcularia, com o
 * `sdrId` de hoje, um mês que já foi pago pela regra antiga. `reabrirMes` recusa antes de ler.
 */
export const mesPodeSerReaberto = (mes) =>
  /^\d{4}-(0[1-9]|1[0-2])$/.test(String(mes ?? '')) && mes >= MES_CORTE_COMISSAO;

/**
 * ★★ Travas do fechamento (03/11/2026, item 2) — só se FECHA mês que JÁ TERMINOU,
 * pelo calendário de SÃO PAULO. O mês corrente e os futuros são recusados (`fecharMes` recusa
 * antes de ler; a tela nem oferece o botão).
 */
export const mesPodeSerFechado = (mes, agora = new Date()) => {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(String(mes ?? ''))) return false;
  const atual = chaveMesDe(agora);
  return Boolean(atual) && mes < atual;
};

const NOMES_DOS_MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho',
  'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** `'2026-10'` → `'outubro/2026'` — o mês como a mensagem para o gestor o escreve. */
export function nomeDoMes(mes) {
  const [ano, m] = String(mes ?? '').split('-').map(Number);
  return NOMES_DOS_MESES[m - 1] ? `${NOMES_DOS_MESES[m - 1]}/${ano}` : String(mes ?? '');
}

// ⚠ O `Intl` põe um espaço SEM QUEBRA depois do "R$" (U+00A0): a mensagem usa o espaço comum,
// para a frase do servidor e a da tela serem a mesma string, byte a byte.
const reais = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/\u00a0/g, ' ');

/**
 * ★★ Travas do fechamento (03/11/2026, item 1) — A MENSAGEM de recebimento em mês FECHADO.
 *
 * ⚠ O dinheiro de um mês fechado não entra em folha nenhuma: o fechamento é um snapshot e não
 * recalcula, e o mês seguinte só lê o próprio mês. Sem aviso, ele some sem rastro. É a mesma
 * frase na prévia da importação (servidor) e no lançamento manual (tela) — por isso mora aqui,
 * no módulo espelhado, e o espelho prova que os dois lados escrevem igual.
 *
 * `parcelas` ausente = um recebimento só (lançamento, correção ou exclusão).
 */
export function mensagemMesFechado({ mes, parcelas = null, valor = 0 } = {}) {
  const nome = nomeDoMes(mes);
  if (parcelas == null) {
    return `Este recebimento é de ${nome}, que já está fechado. Ele não entraria em folha nenhuma. `
      + 'Reabra o mês antes.';
  }
  const um = Number(parcelas) === 1;
  return `${parcelas} parcela${um ? '' : 's'} (${reais(valor)}) ${um ? 'é' : 'são'} de ${nome}, `
    + `que já está fechado. ${um ? 'Ela não entra' : 'Elas não entram'} em folha nenhuma. `
    + 'Reabra o mês antes, ou importe sem essas linhas.';
}

const MES_EM_SP = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit',
});

/**
 * O mês 'AAAA-MM' de uma data, pelo calendário de SÃO PAULO.
 *
 * ★★★ PC-6 P10 — era o calendário LOCAL (`getFullYear`/`getMonth`), e "local" mudava com
 * quem calculava: o navegador (SP) e a Cloud Function (UTC) discordavam das 21h às 24h do
 * último dia do mês. ⚠ Um comparecimento às 22h de 31/10 ia para OUTUBRO na prévia da tela e
 * para NOVEMBRO no snapshot do `fecharMes`. Agora o fuso é fixo, e os dois lados concordam.
 *
 * Nunca `toISOString()` (é UTC). Ver a fronteira do mês em `mesDoRecebimento`.
 */
export function chaveMesDe(data) {
  const d = paraData(data);
  if (!d) return null;
  const p = {};
  for (const parte of MES_EM_SP.formatToParts(d)) p[parte.type] = parte.value;
  return `${p.year}-${p.month}`;
}

/**
 * ★ A FRONTEIRA DO MÊS — onde o dinheiro troca de mês.
 *
 * No relatório real de agosto/2026 há **22 parcelas em 31/08 e 1 em 01/09**:
 * 23 pagamentos exatamente na borda. Errar o recorte manda a comissão de 23
 * pagamentos para o mês errado, e ninguém percebe olhando a tela.
 *
 * DUAS DEFESAS, porque uma só é frágil:
 *
 *  1. **`mes` gravado no recebimento**, derivado da data de ORIGEM ainda como
 *     texto — `"31/08/2026"` do Careflow, `"2026-08-31"` do `<input type=date>`.
 *     Nesse momento não existe `Date` nem fuso: o mês é um fato do documento,
 *     decidido uma vez, por quem tinha a informação na mão.
 *  2. **Reserva pelo calendário de SÃO PAULO da `data`** (PC-6 P10), para os recebimentos
 *     gravados antes deste campo existir.
 *
 * Por que a reserva é segura: os dois caminhos de escrita gravam **meio-dia**
 * (`new Date('...T12:00:00')` na tela, `new Date(a, m, d, 12)` na importação).
 * Meio-dia dá 12 horas de folga para cada lado, e o Brasil usa 3 — o dia
 * continua o mesmo lido em UTC ou em São Paulo. O que quebraria é uma data
 * gravada à MEIA-NOITE UTC: `new Date('2026-09-01')` é `01/09 00:00Z`, que em
 * São Paulo é **31 de agosto** — um pagamento de setembro pago em agosto. Por
 * isso `montarRecebimento` normaliza para meio-dia: fecha essa porta na
 * gravação, e não em cada leitura.
 *
 * E por que NUNCA comparar instantes com `>=`/`<=` contra o começo e o fim do
 * mês: esse recorte depende do fuso de quem calcula, e quem calcula pode ser o
 * navegador (America/Sao_Paulo) ou uma Cloud Function (UTC). Comparando
 * `'2026-08' === '2026-08'` não há fuso nenhum envolvido.
 */
export const mesDoRecebimento = (recebimento) =>
  recebimento?.mes || chaveMesDe(recebimento?.data);

/**
 * Quando o lead compareceu pela primeira vez.
 *
 * ★★ WK (29/set/2026) — A REGRA MUDOU DE CASA, e a razão é de arquitetura.
 *
 * Ela continua sendo ESTA, e continua espelhada contra o servidor. O que mudou é
 * onde ela mora: desceu para `@/leads/contagemComparecimento`, um módulo neutro.
 *
 * ★ POR QUÊ: a WK fez o dashboard e as METAS contarem comparecimento por pessoa,
 * pela mesma regra. Se `metas/calculo.js` importasse daqui, o Placar e o
 * relatório individual passariam a **alcançar o módulo de remuneração** — e há
 * duas guardas de grafo que proíbem exatamente isso (`placar.render.test.js`,
 * `relatorios/individual.test.js`), porque remuneração é o salário de cada um.
 * Medido: as duas ficaram vermelhas na primeira tentativa.
 *
 * ★ A saída não foi copiar a regra (seria a quarta cópia), foi INVERTER a
 * dependência: o módulo neutro define, e remuneração importa de lá. O reexport
 * abaixo mantém `dataComparecimento` disponível neste caminho, que é o que o
 * espelho do servidor compara.
 */
export { creditoComparecimento, dataComparecimento };

/**
 * Comparecimentos do mês, por pessoa — **um por lead**.
 *
 * ⚠ NÃO é o mesmo número da tela de Metas, que conta por AGENDAMENTO
 * (`calculo.js`): um paciente que voltou para uma segunda avaliação aparece
 * duas vezes lá e uma vez aqui. Metas mede atividade; isto paga por paciente
 * que chegou à cadeira. Os dois números vão divergir, e a tela diz isso.
 *
 * ★★★ A-10.4 (16/out/2026) — O CRÉDITO É DE QUEM AGENDOU, não do SDR do lead.
 *
 * ⚠⚠ MUDANÇA DE DINHEIRO, decidida pelo dono. Era `creditoSdr(lead)` — o SDR
 * original. ★ Medido em produção sobre outubro/2026 (73 comparecimentos, um por
 * lead): o crédito sai de **40 / 21 / 12** para **62 / 9 / 1**, mais **1** do
 * gestor. ⚠ 30+ leads trocam de dono; o total não muda.
 *
 * ★ A regra inteira (reserva pelo `sdrId`, `compareceu` mais antigo, robô que não
 * credita) mora em `creditoComparecimento` — módulo neutro, uma definição só.
 *
 * ⚠ A decisão do dono fala de COMPARECIMENTO. Recebimento segue a sua própria régua,
 * `creditoComissao` (PC-6 P8), em `recebimentosDoMes` logo abaixo.
 */
export function comparecimentosDoMes(leads = [], agendamentos = [], mes, equipe = []) {
  const porPessoa = new Map();
  // ★ Índices fora do laço: `dataComparecimento` filtra por `leadId`, então passar
  // a coleção inteira a cada lead dava 8.374 × 2.539 iterações por render (medido
  // em produção, 16/out) — mesma razão do comentário de `indexarAgendamentosPorLead`.
  const porLead = indexarAgendamentosPorLead(agendamentos);
  const daEquipe = indexarEquipe(equipe);
  for (const lead of leads ?? []) {
    const doLead = porLead.get(lead?.id) ?? [];
    const quando = dataComparecimento(lead, doLead);
    if (!quando || chaveMesDe(quando) !== mes) continue;
    const { id, nome } = creditoComparecimento(lead, doLead, daEquipe);
    if (!porPessoa.has(id)) porPessoa.set(id, { id, nome: nome || SEM_RESPONSAVEL, itens: [] });
    porPessoa.get(id).itens.push({
      leadId: lead.id,
      nome: lead.nome || '',
      data: quando,
    });
  }
  return porPessoa;
}

/**
 * Recebimentos do mês, por pessoa.
 *
 * ⚠ O crédito é da SDR **DO LEAD**, nunca de quem lançou ou importou o
 * recebimento. Quem importa a planilha do Careflow importa a de todo mundo.
 *
 * ★★★ PC-6 P8 — qual SDR do lead depende do mês: `creditoComissao` (a atual de
 * outubro/2026 em diante; a original antes).
 */
export function recebimentosDoMes(recebimentos = [], leads = [], mes) {
  const leadPorId = new Map((leads ?? []).map((l) => [l.id, l]));
  const porPessoa = new Map();
  for (const r of recebimentos ?? []) {
    if (mesDoRecebimento(r) !== mes) continue;
    const lead = leadPorId.get(r?.leadId);
    if (!lead) continue;
    const { id, nome } = creditoComissao(lead, mes);
    if (!porPessoa.has(id)) porPessoa.set(id, { id, nome: nome || SEM_RESPONSAVEL, total: 0, itens: [] });
    const linha = porPessoa.get(id);
    const valor = Math.max(0, numero(r.valor));
    linha.total = centavos(linha.total + valor);
    linha.itens.push({
      leadId: r.leadId,
      leadNome: lead.nome || '',
      data: paraData(r.data),
      valor,
      forma: r.forma ?? '',
    });
  }
  return porPessoa;
}

/**
 * O que a tela mostra: o SNAPSHOT do mês fechado, ou o cálculo vivo.
 *
 * Vive aqui, e não dentro do componente, para poder ser TESTADO. A escolha
 * errada é silenciosa e cara: um mês fechado que voltasse a recalcular mostraria
 * um número diferente do que foi pago, e a pessoa descobriria na conversa sobre
 * o pagamento. É a mesma classe de bug do `DialogoEtapa` da V2, que quebrou em
 * produção sem nenhum teste acusar porque a decisão morava só no componente.
 *
 * `status: 'reaberto'` volta a valer a prévia: o gestor reabriu justamente
 * porque o snapshot estava errado.
 */
export function escolherExibicao(snapshot, previa) {
  if (snapshot?.status === 'fechado') return { linha: snapshot, fechado: true };
  return { linha: previa ?? null, fechado: false };
}

/** A configuração é válida? Sem ela a pessoa não entra na folha. */
export const configValida = (config) =>
  Boolean(config) && MODELOS.some((m) => m.id === config.modelo);

/**
 * O pagamento de uma pessoa.
 *
 * O percentual está em PONTOS PERCENTUAIS (`0.7` é 0,7 %), como o dono escreve.
 * No modelo `fixo` ele é ignorado mesmo se estiver preenchido — trocar de modelo
 * não pode fazer um número esquecido voltar a pagar.
 */
export function calcularPessoa(config, { comparecimentos = 0, baseRecebida = 0 } = {}) {
  const valorFixo = centavos(comparecimentos * numero(config?.valorPorComparecimento));
  const percentual = config?.modelo === 'fixo_percentual' ? numero(config?.percentual) : 0;
  const comissao = centavos((numero(baseRecebida) * percentual) / 100);
  return { valorFixo, percentual, comissao, total: centavos(valorFixo + comissao) };
}

/**
 * A folha do mês inteira.
 *
 * `configs` é `{ [uid]: config }`. **Quem não tem config não aparece** — ausência
 * de configuração é "ainda não combinado", não "ganha zero", e uma linha zerada
 * esconderia a diferença justamente de quem precisa vê-la.
 *
 * A linha "Sem responsável" é a exceção: aparece quando há comparecimento ou
 * recebimento de lead sem dono, paga a ninguém, e existe para os totais fecharem
 * com o que a clínica recebeu. Escondê-la faria a soma da tela não bater.
 */
export function consolidarFechamento({
  leads = [],
  agendamentos = [],
  recebimentos = [],
  configs = {},
  equipe = [],
  mes,
} = {}) {
  const comparecimentos = comparecimentosDoMes(leads, agendamentos, mes, equipe);
  const recebidos = recebimentosDoMes(recebimentos, leads, mes);
  const nomePorId = new Map((equipe ?? []).map((u) => [u.id, u.nome]));

  const ids = new Set([...comparecimentos.keys(), ...recebidos.keys()]);
  for (const uid of Object.keys(configs ?? {})) ids.add(uid);

  const linhas = [];
  for (const id of ids) {
    const config = configs?.[id] ?? null;
    const comp = comparecimentos.get(id);
    const rec = recebidos.get(id);

    if (!id) {
      // Sem responsável: só aparece se houver movimento, e nunca paga.
      if (!comp && !rec) continue;
      linhas.push({
        id: '',
        nome: SEM_RESPONSAVEL,
        semConfig: true,
        comparecimentos: comp?.itens.length ?? 0,
        baseRecebida: rec?.total ?? 0,
        valorFixo: 0,
        percentual: 0,
        comissao: 0,
        total: 0,
        detalheComparecimentos: comp?.itens ?? [],
        detalheRecebimentos: rec?.itens ?? [],
      });
      continue;
    }

    if (!configValida(config)) continue;

    const quantidade = comp?.itens.length ?? 0;
    const baseRecebida = rec?.total ?? 0;
    const valores = calcularPessoa(config, { comparecimentos: quantidade, baseRecebida });
    linhas.push({
      id,
      nome: nomePorId.get(id) || comp?.nome || rec?.nome || '',
      modelo: config.modelo,
      // As taxas viajam com a linha para o snapshot poder CONGELÁ-LAS: mudar o
      // percentual de alguém em outubro não pode mexer no extrato de setembro.
      valorPorComparecimento: numero(config.valorPorComparecimento),
      percentual: valores.percentual,
      comparecimentos: quantidade,
      baseRecebida,
      valorFixo: valores.valorFixo,
      comissao: valores.comissao,
      total: valores.total,
      detalheComparecimentos: comp?.itens ?? [],
      detalheRecebimentos: rec?.itens ?? [],
    });
  }

  linhas.sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR'));

  const soma = (campo) => centavos(linhas.reduce((t, l) => t + (Number(l[campo]) || 0), 0));
  return {
    mes,
    linhas,
    totais: {
      comparecimentos: linhas.reduce((t, l) => t + l.comparecimentos, 0),
      baseRecebida: soma('baseRecebida'),
      valorFixo: soma('valorFixo'),
      comissao: soma('comissao'),
      total: soma('total'),
    },
  };
}
